/**
 * GameVideoCard — 2026-10-26 (per Gilles). Phase 1 of video analysis: the
 * arena camera's video of the game, kept with the game, and the highlight
 * clips cut from it (our goals + goals against, and a reel of our goals).
 * The video and the narration do not start together, so the card carries a
 * sync offset: set by hand, or found automatically by lining up the two
 * recordings' sound.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Clapperboard, Download, Loader2, Play, RefreshCw, Trash2, Upload, Wand2, AlertTriangle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { uploadGameVideo, GAME_VIDEO_ACCEPT, uploadError } from '../../utils/recordingUpload.js';

const fmtBytes = (b) => (b >= 1073741824 ? `${(b / 1073741824).toFixed(1)} GB` : `${Math.round(b / 1048576)} MB`);
const fmtMin = (ms) => (ms ? `${Math.round(ms / 60000)} min` : '');

function ClipPlayer({ teamId, gameId, clip, onClose }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null; let dead = false;
    api.get(`/teams/${teamId}/press-box/games/${gameId}/video/clips/${clip.id}/media`, { responseType: 'blob', timeout: 120000 })
      .then(r => { if (dead) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => { if (!dead) toast.error('Could not load the clip'); });
    return () => { dead = true; if (url) URL.revokeObjectURL(url); };
  }, [teamId, gameId, clip.id]);
  return (
    <div style={{ marginTop: 8 }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-bold">{clip.label}</span>
        <button type="button" className="btn-ghost" style={{ padding: '2px 8px', marginLeft: 'auto' }} onClick={onClose}><X size={12} /></button>
      </div>
      {src ? <video src={src} controls autoPlay playsInline style={{ width: '100%', maxHeight: 420, borderRadius: 10, background: '#000' }} />
        : <div className="text-sm flex items-center gap-2" style={{ color: 'var(--text-3)' }}><Loader2 size={13} className="animate-spin" /> Loading the clip…</div>}
    </div>
  );
}

const mmss = (secs) => (secs == null || !Number.isFinite(Number(secs)) ? '—'
  : `${Math.floor(secs / 60)}:${String(Math.floor(secs % 60)).padStart(2, '0')}`);

/**
 * 2026-10-29/30 (per Gilles) — the same game as the two recorders tell it.
 * Four views: AUDIO shows only what the narration gave (who scored, assists,
 * the call's time); VIDEO shows only what the video itself found (the goal
 * moments, the roar and vision checks, the tracker's who-was-on-the-ice,
 * time in zone, the players it read) — never a name the narrator said;
 * SIDE BY SIDE puts the two next to each other with the time delta; and
 * COMBINED is the whole: names from the audio, moments from the video,
 * on-ice from the tracker.
 */
function AudioVsVideo({ teamId, gameId, refreshKey }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState('both');
  const [open, setOpen] = useState(true);   // 2026-10-30 (per Gilles: "where is the comparison?") — open by default
  useEffect(() => {
    if (!open) return;
    let dead = false;
    api.get(`/teams/${teamId}/press-box/games/${gameId}/video/compare`)
      .then(r => { if (!dead) setData(r.data); })
      .catch(() => { if (!dead) setData({ error: true }); });
    return () => { dead = true; };
  }, [open, teamId, gameId, refreshKey]);
  const z = data?.zones?.totals;
  const zTot = z ? z.offS + z.defS + z.neuS : 0;
  const pct = (x) => (zTot ? Math.round((x / zTot) * 100) : 0);
  const showA = view === 'both' || view === 'audio' || view === 'all';
  const showV = view === 'both' || view === 'video' || view === 'all';
  const names = view !== 'video';            // the video view never borrows the narrator's names
  const pill = (id, label, title) => (
    <button key={id} type="button" onClick={() => setView(id)} className="btn-ghost" title={title}
      style={{ padding: '3px 10px', fontWeight: view === id ? 800 : 400, border: view === id ? '1px solid var(--ice)' : undefined }}>
      {label}
    </button>
  );
  // roster labels already start with the number ("#19 Riley North"); never print it twice
  const who = (p) => (p.name ? (String(p.name).startsWith('#') ? p.name : `${p.name} (#${p.number})`) : `#${p.number}`);
  const onIce = (g) => (g.tracker?.onIce || []).map(who);
  const trk = data?.tracker || null;
  return (
    <details className="mb-2" data-audio-vs-video open onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="text-sm" style={{ cursor: 'pointer', fontWeight: 700 }}>
        Audio vs Video — how the two recorders tell the game
      </summary>
      {!data && <div className="text-sm mt-1" style={{ color: 'var(--text-3)' }}>Loading…</div>}
      {data?.error && <div className="text-sm mt-1" style={{ color: 'var(--penalty)' }}>Could not load the comparison.</div>}
      {data && !data.error && (
        <div className="mt-2">
          <div className="flex items-center gap-1 mb-2" style={{ flexWrap: 'wrap' }}>
            {pill('both', 'Side by side', 'audio and video next to each other, with the time difference')}
            {pill('audio', 'Audio', 'only what the narration gave')}
            {pill('video', 'Video', 'only what the video itself found')}
            {pill('all', 'Combined', 'the whole: names from the audio, moments from the video, on-ice from the tracker')}
            <span className="text-xs" style={{ color: 'var(--text-3)', marginLeft: 6 }}>
              {data.matchedRoars ? `${data.matchedRoars} of ${data.goalsTotal} goals matched a roar · ` : ''}the video runs {mmss(Math.abs(data.offsetS))} {data.offsetS >= 0 ? 'behind' : 'ahead of'} the narration
            </span>
          </div>
          {(data.goals || []).map(g => (
            <div key={g.eventId} className="text-xs py-1.5" style={{ borderTop: '1px solid var(--border)' }} data-compare-row>
              <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: g.side === 'them' ? 'var(--penalty)' : 'var(--goal)' }}>
                  {g.fromVideo
                    ? (g.side === 'them' ? 'Goal against — seen on video' : g.side === 'us' ? 'Goal — seen on video' : 'Goal — seen on video, team unclear')
                    : g.side === 'them' ? 'Goal against' : (names ? `Goal — ${g.scorer || 'us'}` : 'Our goal')}
                  {g.fromVideo && <span className="text-xs" style={{ color: 'var(--text-3)', fontWeight: 400 }}> · {Math.round((g.confidence || 0) * 100)}% sure{g.board ? ` · scoreboard ${g.board.before.home}–${g.board.before.guest} → ${g.board.after.home}–${g.board.after.guest}` : ' · scoreboard not readable there'}{g.celebrating ? `, ${g.celebrating} jerseys celebrated` : ''}{g.oursBy ? ` · team by ${g.oursBy}` : ''} · a narration or score sheet names the scorer</span>}
                </span>
                {names && g.side !== 'them' && g.assists?.length > 0 && <span style={{ color: 'var(--text-3)' }}>assist {g.assists.join(', ')}</span>}
                {names && g.period != null && <span style={{ color: 'var(--text-3)' }}>P{g.period}</span>}
                {view === 'both' && g.deltaS != null && (
                  <span style={{ marginLeft: 'auto', fontWeight: 700,
                    color: Math.abs(g.deltaS) <= 6 ? 'var(--goal)' : Math.abs(g.deltaS) <= 20 ? 'var(--puck)' : 'var(--penalty)' }}>
                    Δ {g.deltaS > 0 ? '+' : ''}{g.deltaS} s
                  </span>
                )}
              </div>
              <div className="flex gap-4 mt-0.5" style={{ flexWrap: 'wrap', color: 'var(--text-2)' }}>
                {showA && view !== 'all' && g.audio && <span><strong style={{ color: 'var(--text-3)' }}>audio</strong> {mmss(g.audio?.tapeS)} on the tape{view === 'both' && g.audio?.onVideoS != null ? ` → ${mmss(g.audio.onVideoS)} on the video` : ''}{view === 'audio' && g.audio?.said ? <span style={{ color: 'var(--text-3)' }}> · “{String(g.audio.said).slice(0, 90)}”</span> : null}</span>}
                {showV && view !== 'all' && <span><strong style={{ color: 'var(--text-3)' }}>video</strong> {g.video ? <>{mmss(g.video.s)}{g.video.matchedRoar ? ` · roar${g.video.offBy != null ? ` ±${g.video.offBy}s` : ''}` : ' · shifted with the others'}{g.video.visionConfirmed ? ' · vision ✓' : ''}</> : 'not pinned'}</span>}
                {showV && g.tracker && <span><strong style={{ color: 'var(--text-3)' }}>on the ice</strong> {onIce(g).length ? onIce(g).join(', ') : 'no numbered player seen'}{g.tracker.readByVisionModel?.length ? <span style={{ color: 'var(--text-3)' }}> (#{g.tracker.readByVisionModel.join(', #')} read off the contact sheets)</span> : null}</span>}
                {showV && g.tracker?.scorerGuess && (
                  // 2026-10-31 (per Gilles: "pick up the player who scored") — the vision model's
                  // answer from the frames up to the goal; a GUESS, the narration stays the record
                  <span data-scorer-guess><strong style={{ color: 'var(--text-3)' }}>scorer (video)</strong> {g.tracker.scorerGuess.number != null
                    ? <>{who(g.tracker.scorerGuess)}{g.tracker.scorerGuess.where ? ` ${g.tracker.scorerGuess.where}` : ''} · {Math.round((g.tracker.scorerGuess.confidence || 0) * 100)}% sure{!g.tracker.scorerGuess.onRoster && g.side !== 'them' ? ' · not on our roster' : ''}
                        {view === 'all' && names && g.scorer ? (Number((String(g.scorer).match(/#(\d+)/) || [])[1]) === Number(g.tracker.scorerGuess.number) ? ' · agrees with the narration ✓' : ' · the narration says otherwise') : ''}</>
                    : <span style={{ color: 'var(--text-3)' }}>could not tell who put it in</span>}</span>
                )}
                {view === 'all' && <span><strong style={{ color: 'var(--text-3)' }}>at</strong> {g.video ? mmss(g.video.s) : (g.audio?.onVideoS != null ? mmss(g.audio.onVideoS) : '—')} on the video{g.video?.visionConfirmed ? ' · vision ✓' : ''}</span>}
              </div>
            </div>
          ))}
          {showV && (
            <div className="mt-2 text-xs" data-zone-times>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>Time in zone <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>— only the video can see this{data.zones?.source === 'tracker' ? ' · from the tracker' : ''}</span></div>
              {!z || !zTot ? (
                <div style={{ color: 'var(--text-3)' }}>Not read yet — it comes from the tracker (Re-run video AI).</div>
              ) : (
                <>
                  <div className="flex" style={{ height: 14, borderRadius: 7, overflow: 'hidden', maxWidth: 420 }}>
                    <div style={{ width: `${pct(z.offS)}%`, background: 'var(--goal)' }} title={`offensive ${pct(z.offS)}%`} />
                    <div style={{ width: `${pct(z.neuS)}%`, background: 'var(--text-3)' }} title={`neutral ${pct(z.neuS)}%`} />
                    <div style={{ width: `${pct(z.defS)}%`, background: 'var(--penalty)' }} title={`defensive ${pct(z.defS)}%`} />
                  </div>
                  <div style={{ color: 'var(--text-2)', marginTop: 2 }}>
                    offensive {pct(z.offS)}% ({mmss(z.offS)}) · neutral {pct(z.neuS)}% ({mmss(z.neuS)}) · defensive {pct(z.defS)}% ({mmss(z.defS)})
                    {z.unknownS > 0 ? <span style={{ color: 'var(--text-3)' }}> · {mmss(z.unknownS)} unreadable</span> : null}
                  </div>
                </>
              )}
            </div>
          )}
          {showV && trk && (
            <div className="mt-2 text-xs" data-tracker-block>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>The tracker <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>— who the video itself saw ({trk.tracks} tracks, {(trk.players || []).filter(p => p.number != null).length} numbered)
                {trk.teamColours?.us ? ` · we wore ${trk.teamColours.us}, they wore ${trk.teamColours.them} (${trk.teamColours.decidedBy})` : ''}
                {trk.worker ? ` · ran on ${trk.worker}` : ''}{trk.devices ? ` · ${trk.devices.detect} + ${(trk.devices.ocr || []).join(', ')}` : ''}{trk.visionNote ? ` · ${trk.visionNote}` : ''}</span></div>
              {(trk.players || []).filter(p => p.team === 'us' && p.number != null).length > 0 && (
                <div style={{ color: 'var(--text-2)', padding: '2px 0' }}>
                  <strong style={{ color: 'var(--text-3)' }}>our skaters read </strong>
                  {(trk.players || []).filter(p => p.team === 'us' && p.number != null).sort((x, y) => x.number - y.number)
                    .map(p => `${names && p.name ? p.name + ' ' : ''}#${p.number} (${mmss(p.onIceS)} seen)`).join(' · ')}
                </div>
              )}
              {(trk.plusMinus || []).length > 0 && (
                <div style={{ color: 'var(--text-2)', marginTop: 2 }}>
                  <strong style={{ color: 'var(--text-3)' }}>± </strong>
                  {trk.plusMinus.map(p => `${names && p.name ? `${p.name} ` : ''}#${p.number} ${p.pm > 0 ? '+' : ''}${p.pm}`).join(' · ')}
                </div>
              )}
              {trk.teamColours?.setting && trk.teamColours.us && trk.teamColours.setting !== trk.teamColours.us && (
                <div style={{ color: 'var(--puck)', marginTop: 2 }}>The shirts setting says {trk.teamColours.setting}, but the footage says we wore {trk.teamColours.us} — the footage wins; fix the setting when you can.</div>
              )}
            </div>
          )}
          <div className="mt-2 text-xs" style={{ color: 'var(--text-3)' }}>
            {view === 'audio' && <>Only the narration: who scored, assists, the call’s tape time (a beat after the play).</>}
            {view === 'video' && <>Only the video: when each goal happened (crowd roar, checked by the vision model), who the tracker saw on the ice (numbers it read on the backs; names are the roster’s), time in zone. Nothing here came from the narrator.</>}
            {view === 'both' && <>Audio knows WHO; video knows exactly WHEN and WHERE. Δ is the video’s moment minus the narration’s call projected onto the video clock.</>}
            {view === 'all' && <>The whole picture: names from the audio, moments from the video, on-ice and ± from the tracker.</>}
          </div>
        </div>
      )}
    </details>
  );
}

export default function GameVideoCard({ teamId, gameId, canRecord, canEdit }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [up, setUp] = useState(null);           // { pct }
  const [offset, setOffset] = useState('');
  const [gameStart, setGameStart] = useState('');   // mm:ss our game starts in the video
  const [playing, setPlaying] = useState(null); // clip
  const fileRef = useRef(null);
  const segRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/teams/${teamId}/press-box/games/${gameId}/video`);
      setData(d);
      if (d.video) setOffset(String((d.video.syncOffsetMs || 0) / 1000));
      if (d.video) setGameStart(d.video.gameStartMs != null
        ? `${Math.floor(d.video.gameStartMs / 60000)}:${String(Math.floor((d.video.gameStartMs % 60000) / 1000)).padStart(2, '0')}` : '');
    } catch { setData({ error: true }); }
  }, [teamId, gameId]);
  useEffect(() => { load(); }, [load]);
  const v = data?.video || null;
  // 2026-10-29 (per Gilles: "no indication that it's running") — the video
  // AI pipeline's live state, kept on the video (video_ai)
  const ai = v?.videoAi || null;
  const aiRunning = !!ai?.stage && !['done', 'failed', 'stopped'].includes(ai.stage)
    && ai.startedAt && (Date.now() - Date.parse(ai.startedAt)) < (ai.stage === 'queued' ? 6 * 60 : 90) * 60 * 1000;
  const working = !!v && (v.status === 'storing' || aiRunning || ['queued', 'building'].includes(v.clipsStatus));
  useEffect(() => {
    if (!working) return undefined;
    const t = setInterval(load, aiRunning ? 5000 : 10000);
    return () => clearInterval(t);
  }, [working, aiRunning, load]);

  // 2026-10-28 (per Gilles) — the arena camera splits a game into ~30-min
  // files: several picked at once go up oldest-first and are joined; "Add
  // the next segment" appends to a stored video later.
  const onFiles = async (files, { append = false } = {}) => {
    const parts = [...files].sort((a, b) => (a.lastModified || 0) - (b.lastModified || 0));
    if (!parts.length) return;
    setUp({ pct: 0 });
    try {
      for (let i = 0; i < parts.length; i++) {
        await uploadGameVideo({ teamId, gameId, file: parts[i], append: append || i > 0, more: i < parts.length - 1,
          onProgress: (p) => setUp({ pct: p.pct, seg: parts.length > 1 ? `${i + 1}/${parts.length}` : null }) });
      }
      toast.success(append ? 'Segment uploaded — joining it onto the video' : 'Video uploaded — storing it and cutting the goal clips');
    } catch (e) { toast.error(uploadError(e), { duration: 9000 }); }
    finally { setUp(null); load(); }   // whatever happened, show the real state
  };
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(e.response?.data?.error || 'That did not work'); }
    finally { setBusy(false); }
  };
  const downloadClip = async (clip) => {
    try {
      const r = await api.get(`/teams/${teamId}/press-box/games/${gameId}/video/clips/${clip.id}/media`, { responseType: 'blob', timeout: 300000 });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `${(clip.label || 'clip').replace(/[^\w#–-]+/g, '_')}.mp4`;
      a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    } catch { toast.error('Could not download the clip'); }
  };

  if (!data || data.error) return null;
  if (!v && !canRecord) return null;

  const reel = (data.clips || []).filter(c => c.kind === 'reel');
  const goals = (data.clips || []).filter(c => c.kind !== 'reel');

  return (
    <div className="card mb-4" data-game-video>
      <input ref={fileRef} type="file" accept={GAME_VIDEO_ACCEPT} multiple style={{ display: 'none' }}
        onChange={(e) => { const f = [...(e.target.files || [])]; e.target.value = ''; onFiles(f); }} />
      <input ref={segRef} type="file" accept={GAME_VIDEO_ACCEPT} multiple style={{ display: 'none' }}
        onChange={(e) => { const f = [...(e.target.files || [])]; e.target.value = ''; onFiles(f, { append: true }); }} />
      <div className="flex items-center gap-2 font-bold mb-1">
        <Clapperboard size={16} style={{ color: 'var(--ice)' }} /> Game video
        {v && <span className="text-xs font-normal" style={{ color: 'var(--text-3)' }}>
          {fmtMin(v.durationMs)}{v.bytes ? ` · ${fmtBytes(v.bytes)}` : ''}{v.segments > 1 ? ` · ${v.segments} segments joined` : ''}</span>}
      </div>

      {!v && (
        <div className="text-sm" style={{ color: 'var(--text-2)' }}>
          Upload the arena camera&rsquo;s video of this game — pick BOTH ~30-minute segments at once and they
          are joined in order. It is kept with the game, and goal highlight clips (and a reel of our goals)
          are cut from it using the narration&rsquo;s goal times.
          <div className="mt-2">
            <button type="button" className="btn-ice" disabled={!!up} onClick={() => fileRef.current?.click()} data-video-upload>
              {up ? <><Loader2 size={13} className="animate-spin" /> Uploading{up.seg ? ` ${up.seg}` : ''} {up.pct}%</> : <><Upload size={13} /> Upload the game video (all segments)</>}
            </button>
          </div>
        </div>
      )}

      {v && Array.isArray(v.segmentsInfo) && v.segmentsInfo.length > 0 && (
        <div className="text-xs mb-2" data-video-files>
          {v.segmentsInfo.map((f, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5" style={{ color: 'var(--text-3)' }}>
              <Clapperboard size={11} style={{ flexShrink: 0 }} />
              <span style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{f.name}</span>
              <span style={{ whiteSpace: 'nowrap' }}>{f.at ? new Date(f.at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
            </div>
          ))}
        </div>
      )}
      {v && v.status === 'storing' && <div className="text-sm flex items-center gap-2" style={{ color: 'var(--ice)' }}><Loader2 size={13} className="animate-spin" />
        {v.segments > 1 || up ? 'Joining the segments and storing — a few minutes for a big video…' : 'Storing the video…'}</div>}
      {v && v.status === 'storing' && (
        // 2026-11-02 (per Gilles) — no analysis until the video is stored; say when
        <div className="text-xs mt-1" style={{ color: 'var(--puck)' }} data-storing-wait>
          The video AI waits for this to finish — usually {Math.max(1, Math.round(((v.bytes || 0) / 1e9) * 2))}–{Math.max(3, Math.round(((v.bytes || 0) / 1e9) * 4))} minutes for {v.bytes ? `${(v.bytes / 1e9).toFixed(1)} GB` : 'this size'}; it starts by itself once stored{v.segments > 1 ? ' (a run that was under way on the shorter video has been stopped and will run again)' : ''}.
        </div>
      )}
      {v && v.status === 'failed' && <div className="text-sm" style={{ color: 'var(--penalty)' }}><AlertTriangle size={12} /> {v.error || 'The video could not be stored'} — delete it and upload again.</div>}

      {v && v.status === 'stored' && (
        <>
          {/* 2026-10-28 (per Gilles: "it didn't ask me") — the two things worth
              setting right after the upload, asked for out loud. */}
          {canEdit && !v.jerseyColor && (
            <div className="text-sm mb-2" style={{ padding: '8px 10px', borderRadius: 10, background: 'var(--puck-a10)', border: '1px solid var(--puck-a20)', color: 'var(--text-2)' }} data-video-setup>
              <strong style={{ color: 'var(--puck)' }}>One thing to pick:</strong> <strong>which shirts we wore</strong> this game{data.teamLook?.pants ? <> (the {data.teamLook.pants} pants never change)</> : ''}.
              Game start and sync are worked out automatically from the narration.
            </div>
          )}
          {/* 2026-10-28 (per Gilles) — where the sync stands, in words */}
          <div className="text-xs mb-2" style={{ color: 'var(--text-3)' }} data-sync-status>
            {v.syncSource === 'roars'
              ? <>Synced by matching the goals to the crowd roars ({v.syncConfidence ?? '?'} matched, the video runs {Math.round((v.syncOffsetMs || 0) / 1000)} s behind the narration)
                  {v.gameStartMs != null ? <> · our game starts about {Math.floor(v.gameStartMs / 60000)}:{String(Math.floor((v.gameStartMs % 60000) / 1000)).padStart(2, '0')} into the video</> : null}</>
              : v.syncSource === 'auto'
              ? <>Synced to the narration automatically (confidence {v.syncConfidence ?? '?'})
                  {v.gameStartMs != null ? <> · our game starts about {Math.floor(v.gameStartMs / 60000)}:{String(Math.floor((v.gameStartMs % 60000) / 1000)).padStart(2, '0')} into the video</> : null}</>
              : v.syncSource === 'manual'
              ? <>Sync set by hand{v.gameStartMs != null ? <> · game start {Math.floor(v.gameStartMs / 60000)}:{String(Math.floor((v.gameStartMs % 60000) / 1000)).padStart(2, '0')}</> : null}</>
              : <>Syncs to the narration automatically — it happens right after the game’s stats pass (the camera’s previous-game footage is handled; nothing to type).</>}
          </div>
          {aiRunning && (() => {
            const stepNo = { starting: 0, queued: 0, sync: 1, scoreboard: 2, goals: 3, zones: 4, clips: 5, tracker: 6, on_ice: 7 }[ai.stage] ?? 0;
            const queued = ai.stage === 'queued';
            const minsIn = ai.startedAt ? Math.max(0, Math.round((Date.now() - Date.parse(ai.startedAt)) / 60000)) : null;
            const pctS = typeof ai.stageProgress === 'number' ? `${Math.round(ai.stageProgress * 100)}%` : null;
            const eta = typeof ai.stageEtaS === 'number' ? (ai.stageEtaS < 60 ? 'under a minute left' : `~${Math.round(ai.stageEtaS / 60)} min left`) : null;
            const el = typeof ai.stageElapsedS === 'number' && ai.stageElapsedS >= 60 ? `${Math.round(ai.stageElapsedS / 60)} min on this step` : null;
            return (
              <div className="text-sm mb-2" data-video-ai-banner
                style={{ padding: '10px 12px', borderRadius: 10, background: queued ? 'var(--puck-a10, rgba(255,149,0,0.08))' : 'var(--ice-a10, rgba(56,152,255,0.08))', border: `1px solid ${queued ? 'var(--puck)' : 'var(--ice)'}`, color: 'var(--text-1)' }}>
                <div className="flex items-center gap-2 font-bold" style={{ color: queued ? 'var(--puck)' : 'var(--ice)' }}>
                  <Loader2 size={15} className="animate-spin" />
                  {queued ? 'Video AI — in line' : `Video AI running${stepNo ? ` — step ${stepNo} of 7` : ''}`}
                  {pctS && <span style={{ fontWeight: 600 }}>· {pctS}</span>}
                  {eta && <span style={{ fontWeight: 600 }}>· {eta}</span>}
                  {canEdit && (
                    // 2026-10-31 (per Gilles) — the Stop button: in line = taken out; running = bails at its next check
                    <button type="button" className="btn-ghost" data-stop-video-ai disabled={busy || ai.stopRequested}
                      style={{ marginLeft: 'auto', padding: '4px 10px', color: 'var(--penalty)', borderColor: 'var(--penalty)', fontSize: 12 }}
                      title={queued ? 'Take this game out of the line' : 'Stop the run — what is finished so far stays'}
                      onClick={() => act(async () => {
                        const { data: d } = await api.post(`/teams/${teamId}/press-box/games/${gameId}/video/stop`);
                        toast.success(d.was === 'queued' ? 'Taken out of the line' : 'Stopping — it bails at its next check (a few seconds; the tracker\'s job is cancelled too)');
                      })}>
                      <X size={12} /> {ai.stopRequested ? 'Stopping…' : 'Stop'}
                    </button>
                  )}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-2)' }}>{ai.note || 'Starting…'}</div>
                {/* 2026-10-30 (per Gilles) — which GPUs are working on this step, the minutes, the ETA */}
                {typeof ai.stageProgress === 'number' && (
                  <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden', marginTop: 6, maxWidth: 420 }}>
                    <div style={{ width: `${Math.round(ai.stageProgress * 100)}%`, height: '100%', background: 'var(--ice)' }} />
                  </div>
                )}
                <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                  {!queued && <><strong>GPU:</strong> {ai.gpu || 'none for this step'}{el ? ` · ${el}` : ''}{minsIn != null ? ` · ${minsIn} min since start` : ''}</>}
                  {queued && <>One analysis runs at a time — the GPUs are shared. This game starts by itself when its turn comes; nothing to click.</>}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                  {ai.model ? `Model: ${ai.model}` : ''}{ai.startedAt ? `${ai.model ? ' · ' : ''}${queued ? 'requested' : 'started'} ${new Date(ai.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}
                  {' '}· Sound-sync (CPU) → scoreboard → goal check → zones (5090 via the broker) → clips (CPU) → the tracker ({data.trackerRoute || 'the vision worker'}) → who was on the ice (5090 via the broker).
                </div>
              </div>
            );
          })()}
          {!aiRunning && ai?.stage === 'done' && ai.note && (
            <div className="text-xs mb-2" style={{ color: 'var(--text-3)' }} data-video-ai-last>
              Video AI last ran{ai.finishedAt ? ` ${new Date(ai.finishedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}: {ai.note}
            </div>
          )}
          {!aiRunning && ai?.stage === 'stopped' && ai.note && (
            <div className="text-xs mb-2" style={{ color: 'var(--puck)' }} data-video-ai-stopped>Video AI {ai.note}</div>
          )}
          {!aiRunning && ai?.stage === 'failed' && ai.note && (
            <div className="text-xs mb-2" style={{ color: 'var(--penalty)' }} data-video-ai-last>
              <AlertTriangle size={12} /> Video AI failed: {ai.note}
            </div>
          )}
          <div className="text-sm mb-2" style={{ color: 'var(--text-2)' }}>
            {!aiRunning && ['queued', 'building'].includes(v.clipsStatus) && <span className="flex items-center gap-2" style={{ color: 'var(--ice)' }}><Loader2 size={13} className="animate-spin" /> Cutting the goal clips…</span>}
            {v.clipsStatus === 'none' && (v.clipsNote || 'No goals with a tape time yet — clips are cut once the stats are in.')}
            {v.clipsStatus === 'skipped' && (v.clipsNote || 'Goal clips are turned off in Super admin.')}
            {v.clipsStatus === 'failed' && <span style={{ color: 'var(--penalty)' }}><AlertTriangle size={12} /> {v.clipsNote || 'The clips could not be built'}</span>}
            {v.clipsStatus === 'ready' && v.clipsNote && <span style={{ color: 'var(--puck)' }}><AlertTriangle size={12} /> {v.clipsNote}</span>}
          </div>

          {(data.clips || []).length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {[...reel, ...goals].map(c => (
                <div key={c.id} className="flex items-center gap-2 py-1.5 text-sm" style={{ borderBottom: '1px solid var(--border)' }} data-video-clip={c.kind}>
                  <span style={{ fontWeight: c.kind === 'reel' ? 800 : 600, color: c.kind === 'goal_against' ? 'var(--penalty)' : (c.kind === 'reel' ? 'var(--gold)' : 'var(--goal)') }}>
                    {c.label}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-3)' }}>{c.durationMs ? `${Math.round(c.durationMs / 1000)} s` : ''}{c.bytes ? ` · ${fmtBytes(c.bytes)}` : ''}</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                    <button type="button" className="btn-ghost" style={{ padding: '4px 8px' }} title="Play" onClick={() => setPlaying(playing?.id === c.id ? null : c)}><Play size={13} /></button>
                    <button type="button" className="btn-ghost" style={{ padding: '4px 8px' }} title="Download" onClick={() => downloadClip(c)}><Download size={13} /></button>
                  </span>
                </div>
              ))}
              {playing && <ClipPlayer teamId={teamId} gameId={gameId} clip={playing} onClose={() => setPlaying(null)} />}
            </div>
          )}

          <AudioVsVideo teamId={teamId} gameId={gameId} refreshKey={v.clipsBuiltAt || ''} />
          {canEdit && (
            <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={!!up} data-video-add-segment
                title="The camera cut the game into another ~30-minute file: join it onto this video"
                onClick={() => segRef.current?.click()}>
                <Upload size={13} /> {up ? `Uploading${up.seg ? ` ${up.seg}` : ''} ${up.pct}%` : 'Add the next segment'}
              </button>
              <label className="text-xs flex items-center gap-1" style={{ color: 'var(--text-3)', whiteSpace: 'nowrap' }} title="Which shirts we wore this game — a starting hint; the tracker decides from the footage and the roster numbers it reads">
                Our shirts
                <select className="input-field" style={{ padding: '5px 8px', height: 32 }} value={v.jerseyColor || ''} data-jersey-color
                  onChange={(e) => act(() => api.patch(`/teams/${teamId}/press-box/games/${gameId}/video`, { jerseyColor: e.target.value || null }), 'Saved')}>
                  <option value="">—</option>
                  {(Array.isArray(data.teamLook?.jerseys) && data.teamLook.jerseys.length ? data.teamLook.jerseys : ['blue', 'white']).map(c =>
                    <option key={c} value={c}>{c}</option>)}
                  <option value="other">other</option>
                </select>
              </label>
              {/* 2026-11-02 (per Gilles) — with no narration the video's goals are checked against
                  the arena scoreboard; this says whose goal a score change is */}
              <label className="text-xs flex items-center gap-1" style={{ color: 'var(--text-3)', whiteSpace: 'nowrap' }} title="Which side of the arena scoreboard is us — HOME or GUEST. Used to tell whose goal a score change is when there is no narration.">
                On the board we're
                <select className="input-field" style={{ padding: '5px 8px', height: 32 }} value={v.scoreboardSide || ''} data-scoreboard-side
                  onChange={(e) => act(() => api.patch(`/teams/${teamId}/press-box/games/${gameId}/video`, { scoreboardSide: e.target.value || null }), 'Saved — re-run the video AI to apply it')}>
                  <option value="">—</option>
                  <option value="home">home</option>
                  <option value="guest">guest</option>
                </select>
              </label>
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={busy || aiRunning} data-video-reanalyze
                title="The full video AI: sound-sync, scoreboard checkpoints, the goal check (the model confirms each crowd roar on the GPU), then the clips are cut from the best times."
                onClick={() => act(async () => {
                  const { data: r } = await api.post(`/teams/${teamId}/press-box/games/${gameId}/video/reanalyze`, {}, { timeout: 30000 });
                  toast.success(r?.ahead ? `In line — ${r.ahead} analysis${r.ahead === 1 ? '' : 'es'} ahead of this game (about ${r.etaMin} min). The card follows along.`
                    : 'Video AI started — the banner on this card follows every step.', { duration: 9000 });
                })}>
                {busy || aiRunning ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} {aiRunning ? (ai?.stage === 'queued' ? 'In line…' : 'Video AI running…') : 'Re-run video AI'}
              </button>
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} data-video-debug
                title="Everything the video AI knows about this game (sync, scoreboard timeline, crowd-roar candidates, the model's verdict per moment, clip times) as one JSON file — hand it to support to debug"
                onClick={async () => {
                  try {
                    const r = await api.get(`/teams/${teamId}/press-box/games/${gameId}/video/debug`, { responseType: 'blob', timeout: 60000 });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(r.data);
                    a.download = `video-debug-${gameId.slice(0, 8)}.json`;
                    a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 10000);
                  } catch { toast.error('Could not build the debug file'); }
                }}>
                <Download size={13} /> Debug JSON
              </button>
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px', color: 'var(--penalty)' }} disabled={busy}
                title="Remove the uploaded video file(s) and their clips — the segments are joined into one, so they go together; upload fresh files after"
                onClick={() => { if (window.confirm('Delete this game\'s uploaded video file(s) and their clips? The segments were joined into one, so they are removed together. You can upload fresh files right after.')) act(() => api.delete(`/teams/${teamId}/press-box/games/${gameId}/video`), 'Video deleted — upload fresh files when ready'); }}>
                <Trash2 size={13} /> Delete video
              </button>
            </div>
          )}
          {canEdit && (
            <details className="mt-2" data-video-advanced>
            <summary className="text-xs" style={{ cursor: 'pointer', color: 'var(--text-3)' }}>Advanced — manual sync and re-cutting clips (only if the automatic sync got it wrong)</summary>
            <div className="flex items-center gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
              {/* 2026-10-30 (per Gilles) — out of the main row: Re-run video AI cuts the clips itself; this is only for a stats fix without 20 min of GPU */}
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={busy || aiRunning}
                title="Re-cuts the clips from the times already on file — useful after fixing a goal in the stats, without re-running the AI (no GPU)."
                onClick={() => act(() => api.post(`/teams/${teamId}/press-box/games/${gameId}/video/rebuild-clips`), 'Rebuilding the clips')}>
                <RefreshCw size={13} /> Re-cut clips only (no AI)
              </button>
              <label className="text-xs" style={{ color: 'var(--text-3)' }}
                title="Where OUR game starts in the video — found automatically from the narration; override it here if needed.">
                Our game starts at
                <input className="input-field" style={{ width: 70, marginLeft: 6, padding: '5px 8px' }} placeholder="mm:ss"
                  value={gameStart} onChange={(e) => setGameStart(e.target.value.replace(/[^0-9:]/g, ''))} data-game-start
                  onBlur={() => {
                    const m = gameStart.match(/^(\d{1,3}):([0-5]\d)$/);
                    if (!gameStart) { act(() => api.patch(`/teams/${teamId}/press-box/games/${gameId}/video`, { gameStartMs: null })); return; }
                    if (!m) { toast.error('Use mm:ss, e.g. 27:30'); return; }
                    const ms = (Number(m[1]) * 60 + Number(m[2])) * 1000;
                    act(() => api.patch(`/teams/${teamId}/press-box/games/${gameId}/video`, { gameStartMs: ms }), 'Game start saved');
                  }} />
              </label>
              <label className="text-xs" style={{ color: 'var(--text-3)' }} title="Video time minus narration time for the same moment. Positive = the camera started first.">
                Sync offset (s)
                <input className="input-field" style={{ width: 86, marginLeft: 6, padding: '5px 8px' }} inputMode="numeric"
                  value={offset} onChange={(e) => setOffset(e.target.value.replace(/[^0-9.-]/g, ''))} data-sync-offset />
              </label>
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={busy || offset === '' || Number.isNaN(Number(offset))}
                onClick={() => act(() => api.patch(`/teams/${teamId}/press-box/games/${gameId}/video`, { syncOffsetMs: Math.round(Number(offset) * 1000) }), 'Offset saved — rebuilding the clips')}>
                Save &amp; rebuild
              </button>
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={busy}
                title="Line the video up with the narration automatically by matching the crowd noise on both recordings"
                onClick={() => act(async () => {
                  const { data: d } = await api.post(`/teams/${teamId}/press-box/games/${gameId}/video/align`, {}, { timeout: 300000 });
                  if (d.aligned) { toast.success(`Aligned: the camera ${d.syncOffsetMs >= 0 ? 'started' : 'lagged'} ${Math.abs(d.syncOffsetMs / 1000).toFixed(1)} s ${d.syncOffsetMs >= 0 ? 'before' : 'after'} the narration — rebuilding the clips`); }
                  else toast.error(d.reason || 'Could not align the recordings');
                })}>
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Auto-align
              </button>
            </div>
            <VisionCheck teamId={teamId} gameId={gameId} goals={goals} />
            </details>
          )}
        </>
      )}
    </div>
  );
}

/**
 * 2026-10-31 (per Gilles, after 42 contact sheets came back with no number
 * and no colour) — the VISION CHECK: pick a moment (a goal's, by default),
 * the worker builds the contact sheet of every skater on the ice right
 * then, the vision model reads it, and BOTH come back: the sheet image and
 * the raw answer. One minute instead of a 45-minute run, to see what the
 * model is handed and what it says. Nothing is stored.
 */
function VisionCheck({ teamId, gameId, goals }) {
  const firstGoal = (goals || []).find(c => c.kind === 'goal' && c.tStartMs != null);
  const [t, setT] = useState(firstGoal ? mmss(Math.round(firstGoal.tStartMs / 1000) + 18) : '');
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const [note, setNote] = useState('');
  const run = async () => {
    const m = t.match(/^(\d{1,3}):([0-5]\d)$/);
    if (!m) { toast.error('Use mm:ss, e.g. 21:37'); return; }
    setBusy(true); setOut(null); setNote('starting');
    try {
      // 2026-11-02: the worker pulls the video and the model answers in 1-3 min - start, then poll
      const { data } = await api.post(`/teams/${teamId}/press-box/games/${gameId}/video/vision-check`, { tS: Number(m[1]) * 60 + Number(m[2]) });
      const deadline = Date.now() + 12 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: st } = await api.get(`/teams/${teamId}/press-box/games/${gameId}/video/vision-check/${data.checkId}`);
        setNote(st.note || st.status);
        if (st.status === 'done') { setOut({ ok: true, ...st.result }); return; }
        if (st.status === 'failed') { setOut({ ok: false, error: st.note || 'The vision check failed' }); return; }
      }
      setOut({ ok: false, error: 'No answer in 12 minutes' });
    } catch (e) { setOut({ ok: false, error: e.response?.data?.error || 'The vision check failed' }); }
    finally { setBusy(false); setNote(''); }
  };
  const cells = out?.answer?.cells || [];
  return (
    <div className="mt-3" data-vision-check>
      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
        <span className="text-xs" style={{ color: 'var(--text-3)' }}>Vision check — what the model sees at</span>
        <input className="input-field" style={{ width: 70, padding: '5px 8px' }} placeholder="mm:ss" value={t}
          onChange={(e) => setT(e.target.value.replace(/[^0-9:]/g, ''))} data-vision-check-t />
        <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={busy} onClick={run}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} {busy ? (note ? `${note}…` : 'Working…') : 'Ask the vision model'}
        </button>
        {goals?.length > 0 && (
          <select className="input-field" style={{ width: 'auto', padding: '5px 8px' }} value="" onChange={(e) => { if (e.target.value) setT(mmss(Math.round(Number(e.target.value) / 1000) + 18)); }}>
            <option value="">a goal…</option>
            {goals.filter(c => c.kind === 'goal' && c.tStartMs != null).map(c => <option key={c.id} value={c.tStartMs}>{c.label}</option>)}
          </select>
        )}
      </div>
      {out && !out.ok && <div className="text-xs mt-1" style={{ color: 'var(--penalty)' }}>{out.error}</div>}
      {out?.ok && (
        <div className="mt-2 text-xs" style={{ color: 'var(--text-2)' }}>
          <div>At {mmss(out.tS)} · {out.skaters} skater(s) on the ice{out.ourFamily ? ` · our shirts: ${out.ourShirts} (${out.ourFamily} family)` : ''} ({out.offIce} off-ice detection(s) dropped, ice cover {Math.round((out.iceCover || 0) * 100)}%) · sheet by {out.worker} · {out.model || 'the vision model'} answered in {Math.round((out.ms || 0) / 1000)} s{out.tried?.length ? ` · first tried ${out.tried.join('; ')}` : ''}</div>
          {out.error && <div style={{ color: 'var(--penalty)' }}>{out.error}</div>}
          <div className="flex gap-3 mt-2" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {out.jpeg && <img alt="contact sheet" src={`data:image/jpeg;base64,${out.jpeg}`} style={{ maxWidth: 360, width: '100%', borderRadius: 8, border: '1px solid var(--line)' }} />}
            <div style={{ flex: 1, minWidth: 220 }}>
              {cells.length > 0 && (
                <table className="text-xs" style={{ borderCollapse: 'collapse' }} data-vision-check-cells>
                  <thead><tr style={{ color: 'var(--text-3)' }}><th style={{ textAlign: 'left', paddingRight: 10 }}>cell</th><th style={{ textAlign: 'left', paddingRight: 10 }}>number</th><th style={{ textAlign: 'left', paddingRight: 10 }}>sure</th><th style={{ textAlign: 'left', paddingRight: 10 }}>jersey</th><th style={{ textAlign: 'left', paddingRight: 10 }}>who</th><th style={{ textAlign: 'left', paddingRight: 10 }}>team (jersey)</th><th style={{ textAlign: 'left' }}>team (pants)</th></tr></thead>
                  <tbody>{cells.map(c => {
                    const w = (out.cells || []).find(x => x.id === c.id);
                    return <tr key={c.id}><td style={{ fontWeight: 700 }}>{c.id}</td><td>{c.number ?? '—'}</td><td>{c.number != null ? `${Math.round((c.confidence || 0) * 100)}%` : ''}</td><td>{c.colour || ''}</td><td>{c.role || ''}</td><td style={{ fontWeight: c.teamByColour === 'us' ? 700 : 400 }}>{c.teamByColour || '?'}</td><td>{w?.team || '?'}</td></tr>;
                  })}</tbody>
                </table>
              )}
              <div className="mt-2" style={{ color: 'var(--text-3)' }}>Raw answer{out.answer?.parsed === false ? ' (could not be parsed as JSON)' : ''}:</div>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 180, overflow: 'auto', background: 'var(--bg-2)', padding: 8, borderRadius: 6 }}>{out.answer?.raw || '(nothing)'}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
